import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import {
    createReview,
    getReviewsByUser
} from '../../services/reviewService';
import NotificationToggle from '../../components/NotificationToggle';
import {
    getApprovedPoojaris,
    getPoojarisByCity,
    searchPoojaris,
    getNearbyPoojaris,
    searchNearbyPoojaris
} from '../../services/poojariService';
import {
    getBookingsByUser,
    cancelBooking,
    updateBooking,
    userConfirmComplete
} from '../../services/bookingService';
import {
    createRazorpayOrder,
    verifyRazorpayPayment
} from '../../services/paymentService';
import { formatPaise } from '../../utils/money';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { fetchPoojaTypes } from '../../utils/poojaTypes';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

// COMPLETED bhi active mein rahegi agar paisa baaki hai
const ACTIVE_STATUSES = ['PENDING', 'CONFIRMED'];

const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const keys = ['data', 'content', 'result', 'items'];
    for (const k of keys) {
        if (Array.isArray(payload[k])) return payload[k];
    }
    return [];
};

const Home = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(
        location.state?.tab || 'find');

    const [poojaris, setPoojaris] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loadingPoojaris, setLoadingPoojaris] = useState(true);
    const [loadingBookings, setLoadingBookings] = useState(true);

    const [filterCity, setFilterCity] = useState('');
    const [filterType, setFilterType] = useState('');
    const [poojaTypes, setPoojaTypes] = useState([]);
    const [filterPincode, setFilterPincode] = useState('');
    const [filterLocation, setFilterLocation] = useState('');

    // View details modal
    const [viewBooking, setViewBooking] = useState(null);

    // Konsi booking confirm ho rahi hai (button disable karne ke liye)
    const [confirmingId, setConfirmingId] = useState(null);

    // Payment modal
    const [payBooking, setPayBooking] = useState(null);
    const [payMethod, setPayMethod] = useState('UPI');
    const [paying, setPaying] = useState(false);

    // Cancel modal
    const [cancelId, setCancelId] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // Edit modal
    const [editBooking, setEditBooking] = useState(null);
    const [editForm, setEditForm] = useState({
        poojaDate: '',
        poojaTime: '',
        poojaAddress: '',
        poojaCity: '',
        poojaState: '',
        poojaPincode: '',
        specialInstructions: ''
    });
    const [savingEdit, setSavingEdit] = useState(false);

    // Review modal
    const [reviewBooking, setReviewBooking] = useState(null);
    const [reviewForm, setReviewForm] = useState({
        rating: 5,
        title: '',
        comment: ''
    });
    const [savingReview, setSavingReview] = useState(false);
    const [reviewedIds, setReviewedIds] = useState([]);

    const { token, user } = useAuth();
    const navigate = useNavigate();
        // ⚠️ Kaunsa poojari card khula hai. null = koi nahi.
    //
    // Ek hi khula rehta hai - do khulte to list lambi ho jaati
    // aur user ko scroll karna padta. Accordion ka poora point
    // hi yahi hai ki page chhota rahe.
    const [openPoojariId, setOpenPoojariId] = useState(null);

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        const timer = setTimeout(() => {
            fetchPoojaris();
            fetchBookings();
            fetchMyReviews();
        }, 300);
        return () => clearTimeout(timer);
    }, []);
    
    // Pooja types ek baar laao.
    // ⚠️ Alag useEffect hai - upar wale mein token ka guard hai,
    //    aur ye list PUBLIC hai. Upar wale ke andar daalte to
    //    logout user ko dropdown khaali milta.
    useEffect(() => {
        let alive = true;
        fetchPoojaTypes().then(list => {
            if (alive) setPoojaTypes(list);
        });
        return () => { alive = false; };
    }, []);

    const fetchPoojaris = async () => {
        setLoadingPoojaris(true);
        try {
            const data = await getApprovedPoojaris();
            setPoojaris(normalizeList(data));
        } catch (error) {
            console.log('Error fetching poojaris');
            setPoojaris([]);
        } finally {
            setLoadingPoojaris(false);
        }
    };

    const fetchBookings = async () => {
        setLoadingBookings(true);
        try {
            const userId = user?.id;
            if (!userId) {
                setBookings([]);
                return;
            }
            const data = await getBookingsByUser(userId);
            setBookings(normalizeList(data));
        } catch (error) {
            console.log('Error fetching bookings');
            setBookings([]);
        } finally {
            setLoadingBookings(false);
        }
    };
    useLiveRefresh(fetchBookings);

    // Kaunse bookings ka review already ho chuka hai
    const fetchMyReviews = async () => {
        try {
            const userId = user?.id;
            if (!userId) return;
            const data = await getReviewsByUser(userId);
            const ids = normalizeList(data)
                .filter(r =>
                    r.reviewType === 'USER_TO_POOJARI')
                .map(r => r.bookingId)
                .filter(Boolean);
            setReviewedIds(ids);
        } catch (error) {
            console.log('No reviews found');
            setReviewedIds([]);
        }
    };

    const handleLocationChange = (e) => {
        const v = e.target.value;
        setFilterLocation(v);

        const trimmed = v.trim();
        const isPin = /^\d+$/.test(trimmed);

        setFilterPincode(isPin ? trimmed : '');
        setFilterCity(isPin ? '' : trimmed);
    };

    const handleSearch = async () => {
        if (filterPincode && !/^\d{6}$/.test(filterPincode)) {
            toast.warning('Pincode must be 6 digits');
            return;
        }

        setLoadingPoojaris(true);
        try {
            let data;

            if (filterPincode && filterType) {
                data = await searchNearbyPoojaris(
                    filterPincode, filterType);
            } else if (filterPincode) {
                data = await getNearbyPoojaris(filterPincode);
            } else if (filterCity && filterType) {
                data = await searchPoojaris(
                    filterCity, filterType);
            } else if (filterCity) {
                data = await getPoojarisByCity(filterCity);
            } else {
                data = await getApprovedPoojaris();
            }

            let list = normalizeList(data);

            // Sirf pooja type diya hai toh client side filter
            if (!filterCity && !filterPincode && filterType) {
                list = list.filter(p =>
                    p.specializations?.some(s =>
                        s.toLowerCase()
                            .includes(filterType.toLowerCase())));
            }

            setPoojaris(list);

            if (list.length === 0) {
                toast.info(
                    'No poojaris found. Try a wider search.');
            }
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Search failed');
            setPoojaris([]);
        } finally {
            setLoadingPoojaris(false);
        }
    };

    const clearFilters = () => {
        setFilterCity('');
        setFilterType('');
        setFilterPincode('');
        fetchPoojaris();
    };

    // ===== Booking helpers =====

    // Payment baaki hai toh COMPLETED bhi active mein rahegi
    const isActive = (b) => {
        if (ACTIVE_STATUSES.includes(b.bookingStatus)) {
            return true;
        }
        // Pooja ho gayi lekin paisa baaki hai
        if (b.bookingStatus === 'COMPLETED'
                && b.paymentStatus !== 'COMPLETED') {
            const total = b.bookingAmount || 0;
            const paid = b.paidAmount || 0;
            if (total - paid > 0) return true;
        }
        return false;
    };

        const activeBookings = bookings.filter(isActive);

    // ⚠️ History mein NAYI pooja UPAR (descending).
    // Backend findByUserId() koi order nahi deta - id ke hisaab
    // se aata hai, yaani sabse purani upar. User ko haal ki
    // booking dhoondhne ke liye neeche scroll karna padta tha.
    //
    // ⚠️ Pooja ki date + time se sort, createdAt se nahi - user
    // ke liye "kab ki pooja thi" hi maayne rakhta hai.
    // "2026-09-15" + "07:00:00" string seedha compare ho jaati hai
    // (ISO format), Date object banane ki zaroorat nahi.
    //
    // ⚠️ Date same ho to id se - nayi booking upar.
    const pastBookings = bookings
        .filter(b => !isActive(b))
        .sort((a, b) => {
            const aKey = `${a.poojaDate || ''}T${a.poojaTime || ''}`;
            const bKey = `${b.poojaDate || ''}T${b.poojaTime || ''}`;
            if (aKey !== bKey) {
                return bKey.localeCompare(aKey);
            }
            return (b.id || 0) - (a.id || 0);
        });

    const getHoursLeft = (b) => {
        if (!b.poojaDate || !b.poojaTime) return null;
        const dt = new Date(`${b.poojaDate}T${b.poojaTime}`);
        return (dt - new Date()) / (1000 * 60 * 60);
    };

    // Edit aur cancel dono 24 ghante ke rule pe chalte hain
    const canModify = (b) => {
        if (b.bookingStatus === 'COMPLETED') return false;
        const hrs = getHoursLeft(b);
        return hrs !== null && hrs >= 24;
    };

    const getDueAmount = (b) => {
        const paid = b.paidAmount || 0;
        const total = b.bookingAmount || 0;
        if (paid > 0) return b.remainingAmount || 0;
        if (b.paymentMode === 'PARTIAL_ADVANCE') {
            return b.advanceAmount || 0;
        }
        return total;
    };

    const getPaymentType = (b) => {
        const paid = b.paidAmount || 0;
        if (paid > 0) return 'BALANCE';
        if (b.paymentMode === 'PARTIAL_ADVANCE') return 'ADVANCE';
        return 'FULL';
    };

    // Pay after pooja mein bhi pehle pay kar sakte hain
    const showPayButton = (b) => {
        if (b.paymentStatus === 'COMPLETED') return false;
        if (b.onlinePaymentAllowed === false) return false;
        return getDueAmount(b) > 0;
    };

    const paymentModeLabel = (mode) => {
        if (mode === 'PAY_AFTER_POOJA') return 'Pay after pooja';
        if (mode === 'FULL_ADVANCE') return 'Full advance';
        if (mode === 'PARTIAL_ADVANCE') return 'Token advance';
        return mode || '-';
    };

    /**
     * Razorpay Checkout kholo - booking ke baad payment ke liye.
     *
     * ⚠️ Purana makePayment() HATA diya. Wo bina kisi asli payment
     * ke booking ko "paid" mark kar deta tha - koi bhi browser
     * console se free booking le sakta tha.
     *
     * ⚠️ Amount NAHI bhejte - backend booking se nikalta hai.
     * getDueAmount() sirf UI mein dikhane ke liye hai; asli amount
     * backend hi tay karta hai paymentType ke hisaab se.
     */
    const confirmPayment = async () => {
        if (!payBooking) return;

        if (!window.Razorpay) {
            toast.error('Payment system not ready. '
                + 'Please refresh and try again.');
            return;
        }

        setPaying(true);
        try {
            const order = await createRazorpayOrder(
                payBooking.id,
                getPaymentType(payBooking)
            );

            const options = {
                key: order.razorpayKeyId,
                order_id: order.razorpayOrderId,
                amount: order.amountInPaise,
                currency: order.currency || 'INR',
                name: 'Yajman',
                description: order.poojaType || 'Pooja booking',

                prefill: {
                    name: order.userName || '',
                    email: order.userEmail || '',
                    contact: order.userPhone || ''
                },

                theme: { color: RED },

                // ⚠️ Handler chalne ka matlab payment confirm hona
                // NAHI hai. Backend signature verify karega tabhi
                // paisa maana jayega.
                handler: async (response) => {
                    try {
                        await verifyRazorpayPayment(response);
                        toast.success('Payment successful');
                        setPayBooking(null);
                        fetchBookings();
                    } catch (err) {
                        toast.error(err.response?.data?.message
                            || 'Payment verification failed. If money '
                            + 'was deducted it will be refunded.');
                    } finally {
                        setPaying(false);
                    }
                },

                modal: {
                    ondismiss: () => {
                        setPaying(false);
                        toast.info('Payment cancelled');
                    }
                }
            };

            const rzp = new window.Razorpay(options);

            rzp.on('payment.failed', (response) => {
                setPaying(false);
                toast.error(response.error?.description
                    || 'Payment failed. Please try again.');
            });

            rzp.open();

        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not start payment');
            setPaying(false);
        }
    };

    /**
     * User ne "Pooja complete" confirm kiya.
     *
     * ⚠️ Ye booking ka DOOSRA aadha hissa hai. Poojari pehle
     * "Mark as done" dabata hai, phir user confirm karta hai -
     * TAB booking COMPLETED hoti hai aur poojari ka paisa release
     * hota hai.
     *
     * User 3 din tak confirm na kare to auto-release ho jaata hai
     * (scheduled job), taaki poojari ka paisa hamesha ke liye na
     * atke. Par wo 3 din ka intezaar hai - button se turant.
     */
    const handleUserConfirm = async (booking) => {
        setConfirmingId(booking.id);
        try {
            await userConfirmComplete(booking.id);
            toast.success('Thank you! Booking completed.');
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not confirm. Please try again.');
        } finally {
            setConfirmingId(null);
        }
    };

    const confirmCancel = async () => {
        if (!cancelReason.trim()) {
            toast.warning('Enter a cancellation reason');
            return;
        }
        setCancelling(true);
        try {
            await cancelBooking(cancelId, cancelReason);
            toast.success('Booking cancelled');
            setCancelId(null);
            setCancelReason('');
            setViewBooking(null);
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Cancellation failed');
        } finally {
            setCancelling(false);
        }
    };

    const openEditModal = (b) => {
        setViewBooking(null);
        setEditBooking(b);
        setEditForm({
            poojaDate: b.poojaDate || '',
            poojaTime: b.poojaTime || '',
            poojaAddress: b.poojaAddress || '',
            poojaCity: b.poojaCity || '',
            poojaState: b.poojaState || '',
            poojaPincode: b.poojaPincode || '',
            specialInstructions: b.specialInstructions || ''
        });
    };

    const confirmEdit = async (e) => {
        e.preventDefault();
        setSavingEdit(true);
        try {
            await updateBooking(editBooking.id, editForm);
            toast.success('Booking updated');
            setEditBooking(null);
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Update failed');
        } finally {
            setSavingEdit(false);
        }
    };

    // ===== Review handlers =====

    const openReviewModal = (b) => {
        setReviewBooking(b);
        setReviewForm({ rating: 5, title: '', comment: '' });
    };

    const confirmReview = async (e) => {
        e.preventDefault();
        if (!reviewForm.title.trim()) {
            toast.warning('Please add a title');
            return;
        }
        setSavingReview(true);
        try {
            await createReview({
                userId: reviewBooking.userId,
                poojariId: reviewBooking.poojariId,
                bookingId: reviewBooking.id,
                reviewType: 'USER_TO_POOJARI',
                rating: parseInt(reviewForm.rating),
                title: reviewForm.title,
                comment: reviewForm.comment
            });
            toast.success('Thank you for your review');
            setReviewedIds([...reviewedIds, reviewBooking.id]);
            setReviewBooking(null);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not submit review');
            fetchMyReviews();
        } finally {
            setSavingReview(false);
        }
    };

    const statusColor = (status) => {
        const map = {
            PENDING: { bg: '#FFF4E0', text: '#8A5300' },
            CONFIRMED: { bg: '#E6F1FB', text: '#0C447C' },
            COMPLETED: { bg: '#EAF3DE', text: '#27500A' },
            CANCELLED: { bg: '#FCEBEB', text: '#791F1F' },
            REJECTED: { bg: '#EFEFEF', text: '#444' }
        };
        return map[status] || { bg: '#EFEFEF', text: '#444' };
    };

        // ===== Live sorting + filtering =====
    const displayPoojaris = useMemo(() => {

        const pin = filterPincode.trim();
        const city = filterCity.trim().toLowerCase();
        const type = filterType.trim().toLowerCase();

        // Pehle FILTER - pooja type
        let list = poojaris;

        if (type) {
            list = list.filter(p =>
                p.services?.some(s =>
                    s.poojaType?.toLowerCase().includes(type))
                || p.specializations?.some(sp =>
                    sp?.toLowerCase().includes(type)));
        }

        // Phir SORT - location
        if (!pin && !city) return list;

        // ⚠️ Pincode ke pehle 3 digit = ilaaka. Odisha mein 751xxx
        // Bhubaneswar hai, 758xxx Bhadrak. Isliye 3 digit match
        // hone se "aas-paas" ka matlab nikal aata hai - bina
        // latitude/longitude ke.
        const prefix = pin.substring(0, 3);

        const rank = (p) => {
            // Exact pincode - sabse upar
            if (pin && p.pincode === pin) return 0;

            // ⚠️ City match bhi 1 hi rank pe - kyunki ek hi
            // sheher mein alag-alag pincode hote hain. City se
            // dhoondhne wala poore sheher ko "paas" maanta hai.
            if (city && p.city?.toLowerCase().includes(city)) return 1;

            // Aas-paas ka pincode
            if (pin && pin.length >= 3
                && p.pincode?.startsWith(prefix)) return 1;

            if (!p.pincode) return 3;
            return 2;
        };

        return [...list].sort((a, b) => {
            const ra = rank(a);
            const rb = rank(b);
            if (ra !== rb) return ra - rb;
            // Barabar rank pe zyada rating wala upar
            return (b.rating || 0) - (a.rating || 0);
        });

    }, [poojaris, filterPincode, filterCity, filterType]);

    const exactCount = filterPincode
        ? poojaris.filter(p => p.pincode === filterPincode).length
        : 0;

    const areaCount = filterPincode.length >= 3
        ? poojaris.filter(p =>
            p.pincode?.startsWith(filterPincode.substring(0, 3)))
            .length
        : 0;

    const tabs = [
        { key: 'find', label: 'Find Poojari', count: null },
        {
            key: 'active',
            label: 'Active Bookings',
            count: activeBookings.length
        },
        { key: 'history', label: 'History', count: null }
    ];

    return (
        <div style={{ backgroundColor: '#FFF5F5',
            minHeight: '80vh' }}>
            <div className="container py-4">

                {/* ===== TABS ===== */}
                <div className="d-flex gap-1 p-1 mb-4"
                    style={{
                        backgroundColor: 'white',
                        border: '1px solid #FFD700',
                        borderRadius: '12px'
                    }}>
                    {tabs.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            className="flex-fill border-0 fw-bold"
                            style={{
                                padding: '10px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                backgroundColor:
                                    activeTab === t.key
                                    ? RED : 'transparent',
                                color: activeTab === t.key
                                    ? GOLD : '#666'
                            }}>
                            {t.label}
                            {t.count > 0 && (
                                <span className="badge ms-2"
                                    style={{
                                        backgroundColor: GOLD,
                                        color: DARK_RED,
                                        fontSize: '11px'
                                    }}>
                                    {t.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                <NotificationToggle role="USER" />

                {/* ===== TAB 1 : FIND POOJARI ===== */}
                {activeTab === 'find' && (
                    <>
                        <div className="mb-4">
                                                        <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px'
                            }}>

                                {/* ⚠️ EK box - pincode ya city dono.
                                    Do alag box the to user sochta tha ki
                                    kaunse mein kya daale. Andar khud tay
                                    ho jaata hai: sirf ankein = pincode,
                                    warna city. */}
                                <div style={{
                                    flex: '1 1 100%',
                                    minWidth: 0
                                }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Pincode or city"
                                        value={filterLocation}
                                        onChange={handleLocationChange}
                                        style={{
                                            borderColor: GOLD,
                                            borderRadius: '10px'
                                        }}
                                    />
                                </div>

                                {/* ⚠️ Pooja type aur Search EK LINE mein
                                    rehte hain, mobile pe bhi. Teen box ek
                                    line mein nahi samate the, do samate
                                    hain. */}
                                <div style={{
                                    flex: '1 1 0',
                                    minWidth: '130px'
                                }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Any pooja"
                                        list="poojaTypesList"
                                        value={filterType}
                                        onChange={e =>
                                            setFilterType(e.target.value)}
                                        style={{
                                            borderColor: GOLD,
                                            borderRadius: '10px'
                                        }}
                                    />
                                    <datalist id="poojaTypesList">
                                        {poojaTypes.map(p => (
                                            <option key={p} value={p} />
                                        ))}
                                    </datalist>
                                </div>

                                {/* ⚠️ Search chhota rakha hai - ye MUKHYA
                                    cheez nahi hai.

                                    Live sorting turant chalti hai jaise hi
                                    user type kare. Search ka kaam bas itna
                                    hai ki server se AUR poojari laaye - jo
                                    abhi load hi nahi hue. */}
                                <button
                                    className="btn fw-bold"
                                    style={{
                                        backgroundColor: RED,
                                        color: GOLD,
                                        borderRadius: '10px',
                                        padding: '0 18px',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onClick={handleSearch}
                                    disabled={loadingPoojaris}>
                                    {loadingPoojaris ? '...' : 'Search'}
                                </button>

                                {(filterLocation || filterType) && (
                                    <button
                                        className="btn"
                                        style={{
                                            border: `1px solid ${GOLD}`,
                                            borderRadius: '10px',
                                            color: '#888',
                                            padding: '0 12px'
                                        }}
                                        onClick={() => {
                                            setFilterLocation('');
                                            setFilterType('');
                                            clearFilters();
                                        }}>
                                        &#10005;
                                    </button>
                                )}
                            </div>
                            <small className="text-muted d-block mt-2">
                                Type your pincode — poojaris near you
                                move to the top instantly. Press Search
                                to load more from your area.
                            </small>
                        </div>

                        {loadingPoojaris ? (
                            <div className="text-center py-5">
                                <div className="spinner-border"
                                    style={{ color: RED }}></div>
                            </div>
                        ) : displayPoojaris.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 style={{ color: DARK_RED }}>
                                    No poojaris found
                                </h5>
                                <p className="text-muted">
                                    Try a different pincode, city
                                    or pooja type
                                </p>
                            </div>
                        ) : (
                            <>
                                {filterPincode.length >= 3 && (
                                    <p className="mb-3"
                                        style={{
                                            fontSize: '14px',
                                            color: DARK_RED
                                        }}>
                                        <strong>{exactCount}</strong>{' '}
                                        in your exact pincode ·{' '}
                                        <strong>{areaCount}</strong>{' '}
                                        in your area
                                    </p>
                                )}

                                <div className="row g-3">
                                    {displayPoojaris.map(p => {

                                        const minPrice =
                                            p.services?.length
                                                ? Math.min(...p.services
                                                    .map(s => s.priceInPaise))
                                                : null;

                                        const isOpen = openPoojariId === p.id;

                                        return (
                                            <div key={p.id}
                                                style={{
                                                    backgroundColor: 'white',
                                                    border: `1px solid ${GOLD}`,
                                                    borderRadius: '12px',
                                                    marginBottom: '10px',
                                                    overflow: 'hidden'
                                                }}>

                                                {/* ⚠️ Poora row tap karne pe khulta hai, sirf
                                                    chevron nahi. Chhota target mobile pe
                                                    dhoondhna padta hai - poora row aasan hai. */}
                                                <div
                                                    onClick={() => setOpenPoojariId(
                                                        isOpen ? null : p.id)}
                                                    style={{
                                                        padding: '12px 14px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        gap: '10px'
                                                    }}>

                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div style={{
                                                            fontSize: '15px',
                                                            fontWeight: 700,
                                                            color: DARK_RED,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {p.firstName} {p.lastName}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#777',
                                                            marginTop: '3px'
                                                        }}>
                                                            {p.city} {p.pincode}
                                                            {p.rating > 0 &&
                                                                ` \u00B7 \u2B50 ${p.rating}`}
                                                            {p.experienceYears > 0 &&
                                                                ` \u00B7 ${p.experienceYears} yrs`}
                                                        </div>
                                                        {!p.isAvailable && (
                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: '#999',
                                                                marginTop: '3px'
                                                            }}>
                                                                Currently unavailable
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div style={{
                                                        textAlign: 'right',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {minPrice != null && (
                                                            <div style={{
                                                                fontSize: '15px',
                                                                fontWeight: 700,
                                                                color: RED
                                                            }}>
                                                                {formatPaise(minPrice)}+
                                                            </div>
                                                        )}
                                                        <div style={{
                                                            fontSize: '16px',
                                                            color: RED,
                                                            marginTop: '2px',
                                                            transform: isOpen
                                                                ? 'rotate(180deg)'
                                                                : 'none',
                                                            transition: 'transform 0.2s'
                                                        }}>
                                                            &#9662;
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* ⚠️ Poojas ki list - yahi is redesign ka
                                                    asli faayda hai.
                                                    
                                                    Pehle user ko har poojari ka page kholna
                                                    padta tha ye dekhne ke liye ki wo kaunsi
                                                    pooja karta hai aur kitne mein. 10 poojari
                                                    = 10 page. Ab sab yahin dikhta hai.
                                                    
                                                    Aur Book seedha us POOJA pe hai - booking
                                                    page pe wo pehle se chuni hui aayegi,
                                                    dobara chunni nahi padegi. */}
                                                {isOpen && (
                                                    <div style={{
                                                        borderTop: '1px solid #F0E0E0',
                                                        padding: '4px 14px 12px'
                                                    }}>
                                                        {p.services?.length ? (
                                                            p.services.map((s, i) => (
                                                                <div key={i}
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        alignItems: 'center',
                                                                        padding: '8px 0',
                                                                        borderBottom:
                                                                            i < p.services.length - 1
                                                                                ? '1px solid #FAF0F0'
                                                                                : 'none'
                                                                    }}>
                                                                    <span style={{
                                                                        fontSize: '13px',
                                                                        color: '#444',
                                                                        minWidth: 0,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {s.poojaType}
                                                                    </span>
                                                                    <span style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '10px',
                                                                        whiteSpace: 'nowrap',
                                                                        marginLeft: '10px'
                                                                    }}>
                                                                        <span style={{
                                                                            fontSize: '13px',
                                                                            fontWeight: 600,
                                                                            color: '#444'
                                                                        }}>
                                                                            {formatPaise(
                                                                                s.priceInPaise)}
                                                                        </span>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigate(
                                                                                    `/book/${p.id}`
                                                                                    + `?pooja=`
                                                                                    + encodeURIComponent(
                                                                                        s.poojaType));
                                                                            }}
                                                                            disabled={!p.isAvailable}
                                                                            style={{
                                                                                backgroundColor:
                                                                                    p.isAvailable
                                                                                        ? RED
                                                                                        : '#CCC',
                                                                                color: GOLD,
                                                                                border: 'none',
                                                                                borderRadius: '6px',
                                                                                padding: '5px 14px',
                                                                                fontSize: '12px',
                                                                                fontWeight: 700,
                                                                                cursor: p.isAvailable
                                                                                    ? 'pointer'
                                                                                    : 'not-allowed'
                                                                            }}>
                                                                            Book
                                                                        </button>
                                                                    </span>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p style={{
                                                                fontSize: '12px',
                                                                color: '#999',
                                                                margin: '8px 0'
                                                            }}>
                                                                No poojas listed yet
                                                            </p>
                                                        )}

                                                        {/* ⚠️ "View" purana raasta hai - poora
                                                            profile dekhna ho to. Dono raaste
                                                            rehne chahiye: jise jaldi book karna
                                                            hai wo upar se kare, jise dekh ke
                                                            karna hai wo yahan se. */}
                                                        <div style={{
                                                            textAlign: 'center',
                                                            paddingTop: '10px'
                                                        }}>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/poojari/${p.id}`);
                                                                }}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: RED,
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    cursor: 'pointer'
                                                                }}>
                                                                View full profile &rarr;
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}

                {/* ===== TAB 2 : ACTIVE BOOKINGS ===== */}
                {activeTab === 'active' && (
                    loadingBookings ? (
                        <div className="text-center py-5">
                            <div className="spinner-border"
                                style={{ color: RED }}></div>
                        </div>
                    ) : activeBookings.length === 0 ? (
                        <div className="text-center py-5">
                            <h5 style={{ color: DARK_RED }}>
                                No active bookings
                            </h5>
                            <p className="text-muted">
                                Book a poojari to get started
                            </p>
                            <button
                                className="btn fw-bold"
                                style={{
                                    backgroundColor: RED,
                                    color: GOLD
                                }}
                                onClick={() => setActiveTab('find')}>
                                Find a Poojari
                            </button>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {activeBookings.map(b => {
                                const paid = b.paidAmount || 0;
                                const total = b.bookingAmount || 0;
                                const pct = total > 0
                                    ? Math.round((paid / total) * 100)
                                    : 0;
                                const sc = statusColor(
                                    b.bookingStatus);

                                return (
                                    <div key={b.id} className="col-12">
                                        <div className="card"
                                            style={{
                                                borderRadius: '12px',
                                                border: '1px solid #FFD700'
                                            }}>
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <h6 className="mb-1 fw-bold"
                                                            style={{
                                                                color: DARK_RED
                                                            }}>
                                                            {b.poojaType}
                                                        </h6>
                                                        <p className="text-muted mb-0"
                                                            style={{
                                                                fontSize: '13px'
                                                            }}>
                                                            📅 {b.poojaDate} at {b.poojaTime}
                                                        </p>
                                                        <p className="text-muted mb-0"
                                                            style={{
                                                                fontSize: '13px'
                                                            }}>
                                                            📍 {b.poojaAddress}, {b.poojaCity}
                                                        </p>
                                                        
                                                            {/* ⚠️ SAMPARK - sirf pooja se 24
                                                                ghante pehle. Backend tay
                                                                karta hai. */}
                                                            {b.contactPhone && (
                                                                <>
                                                                    <a href={`tel:${b.contactPhone}`}
                                                                        className="btn btn-sm fw-bold mt-2"
                                                                        style={{
                                                                            backgroundColor: '#2E7D32',
                                                                            color: 'white',
                                                                            borderRadius: '8px',
                                                                            fontSize: '13px'
                                                                        }}>
                                                                        📞 Call {b.contactName || 'poojari'}
                                                                    </a>
                                                                    <div className="text-muted mt-1"
                                                                        style={{ fontSize: '11px' }}>
                                                                        {b.contactPhone}
                                                                    </div>
                                                                </>
                                                            )}
                                                    </div>
                                                    <span className="badge"
                                                        style={{
                                                            backgroundColor: sc.bg,
                                                            color: sc.text
                                                        }}>
                                                        {b.bookingStatus}
                                                    </span>
                                                </div>

                                                {/* Payment progress */}
                                                <div className="p-3 mb-3"
                                                    style={{
                                                        backgroundColor: '#FFF5F5',
                                                        borderRadius: '8px'
                                                    }}>
                                                    <div className="d-flex justify-content-between mb-2"
                                                        style={{
                                                            fontSize: '13px'
                                                        }}>
                                                        <span className="text-muted">
                                                            Paid
                                                        </span>
                                                        <strong>
                                                            {formatPaise(paid)} of {formatPaise(total)}
                                                        </strong>
                                                    </div>
                                                    <div style={{
                                                        height: '6px',
                                                        backgroundColor: '#EEE',
                                                        borderRadius: '3px',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div style={{
                                                            width: `${pct}%`,
                                                            height: '100%',
                                                            backgroundColor: '#639922'
                                                        }}></div>
                                                    </div>
                                                    <p className="mb-0 mt-2 text-muted"
                                                        style={{
                                                            fontSize: '12px'
                                                        }}>
                                                        {getDueAmount(b) > 0
                                                            ? `${formatPaise(getDueAmount(b))} due · ${paymentModeLabel(b.paymentMode)}`
                                                            : 'Fully paid'}
                                                    </p>
                                                </div>

                                                {/* Pooja ho gayi, paisa baaki */}
                                                {b.bookingStatus === 'COMPLETED'
                                                    && getDueAmount(b) > 0 && (
                                                    <div className="p-3 mb-3"
                                                        style={{
                                                            backgroundColor: '#FCEBEB',
                                                            borderRadius: '8px'
                                                        }}>
                                                        <p className="mb-0"
                                                            style={{
                                                                color: '#791F1F',
                                                                fontSize: '13px'
                                                            }}>
                                                            🙏 Your pooja is complete.
                                                            Please pay {formatPaise(getDueAmount(b))}
                                                            {' '}to the poojari.
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Action buttons */}
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {showPayButton(b) && (
                                                        <button
                                                            className="btn btn-sm fw-bold"
                                                            style={{
                                                                backgroundColor: RED,
                                                                color: GOLD
                                                            }}
                                                            onClick={() => {
                                                                setPayBooking(b);
                                                                setPayMethod('UPI');
                                                            }}>
                                                            Pay {formatPaise(getDueAmount(b))}
                                                        </button>
                                                    )}
                                                    {/* ⚠️ Poojari ne "Mark as done" daba diya,
                                                        ab user ka confirm baaki hai. Dono ke
                                                        confirm karne pe hi booking COMPLETED
                                                        hoti hai aur poojari ka paisa release
                                                        hota hai.

                                                        Bina is button ke har booking 3 din
                                                        auto-release ka intezaar karti. */}
                                                    {b.poojariConfirmedAt
                                                        && !b.userConfirmedAt
                                                        && !b.completedAt && (
                                                        <button
                                                            className="btn btn-sm fw-bold"
                                                            style={{
                                                                backgroundColor: '#3B6D11',
                                                                color: 'white'
                                                            }}
                                                            disabled={confirmingId === b.id}
                                                            onClick={() =>
                                                                handleUserConfirm(b)}>
                                                            {confirmingId === b.id
                                                                ? 'Please wait...'
                                                                : '✅ Confirm pooja done'}
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm fw-bold"
                                                        style={{
                                                            border: `1px solid ${RED}`,
                                                            color: RED,
                                                            backgroundColor:
                                                                'transparent'
                                                        }}
                                                        onClick={() =>
                                                            setViewBooking(b)}>
                                                        View details
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}

                {/* ===== TAB 3 : HISTORY ===== */}
                {activeTab === 'history' && (
                    loadingBookings ? (
                        <div className="text-center py-5">
                            <div className="spinner-border"
                                style={{ color: RED }}></div>
                        </div>
                    ) : pastBookings.length === 0 ? (
                        <div className="text-center py-5">
                            <h5 style={{ color: DARK_RED }}>
                                No past bookings
                            </h5>
                            <p className="text-muted">
                                Your completed and cancelled
                                bookings will appear here
                            </p>
                        </div>
                    ) : (
                        <div className="card"
                            style={{
                                borderRadius: '12px',
                                border: '1px solid #FFD700',
                                overflow: 'hidden'
                            }}>
                            {pastBookings.map((b, i) => {
                                const sc = statusColor(
                                    b.bookingStatus);
                                const alreadyReviewed =
                                    reviewedIds.includes(b.id);
                                return (
                                    <div key={b.id}
                                        className="d-flex justify-content-between align-items-center p-3"
                                        style={{
                                            borderBottom:
                                                i < pastBookings.length - 1
                                                ? '1px solid #eee'
                                                : 'none'
                                        }}>
                                        <div>
                                            <p className="mb-1 fw-bold"
                                                style={{
                                                    color: DARK_RED,
                                                    fontSize: '14px'
                                                }}>
                                                {b.poojaType}
                                            </p>
                                            <p className="mb-0 text-muted"
                                                style={{
                                                    fontSize: '12px'
                                                }}>
                                                {b.poojaDate} · {formatPaise(b.paidAmount || 0)} paid
                                                {b.paymentStatus === 'REFUNDED'
                                                    && ' · refunded'}
                                            </p>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge"
                                                style={{
                                                    backgroundColor: sc.bg,
                                                    color: sc.text
                                                }}>
                                                {b.bookingStatus}
                                            </span>
                                            {b.bookingStatus === 'COMPLETED'
                                                && (alreadyReviewed ? (
                                                <span className="badge"
                                                    style={{
                                                        backgroundColor: '#EAF3DE',
                                                        color: '#27500A'
                                                    }}>
                                                    ✅ Reviewed
                                                </span>
                                            ) : (
                                                <button
                                                    className="btn btn-sm fw-bold"
                                                    style={{
                                                        border: `1px solid ${RED}`,
                                                        color: RED,
                                                        fontSize: '12px',
                                                        backgroundColor:
                                                            'transparent'
                                                    }}
                                                    onClick={() =>
                                                        openReviewModal(b)}>
                                                    ⭐ Write Review
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}
            </div>

            {/* ===== VIEW DETAILS MODAL ===== */}
            {viewBooking && (
                <>
                    <div onClick={() => setViewBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%', maxWidth: '520px',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header d-flex justify-content-between align-items-center"
                                style={{
                                    backgroundColor: DARK_RED,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                <span className="fw-bold"
                                    style={{ color: GOLD }}>
                                    Booking #{viewBooking.id}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setViewBooking(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: GOLD,
                                        fontSize: '24px',
                                        lineHeight: 1,
                                        cursor: 'pointer'
                                    }}>
                                    ×
                                </button>
                            </div>

                            <div className="card-body p-4">

                                <h6 className="fw-bold mb-1"
                                    style={{ color: DARK_RED }}>
                                    {viewBooking.poojaType}
                                </h6>
                                <span className="badge mb-3"
                                    style={{
                                        backgroundColor: statusColor(
                                            viewBooking.bookingStatus).bg,
                                        color: statusColor(
                                            viewBooking.bookingStatus).text
                                    }}>
                                    {viewBooking.bookingStatus}
                                </span>

                                {[
                                    {
                                        label: 'Date & time',
                                        value: `${viewBooking.poojaDate} at ${viewBooking.poojaTime}`
                                    },
                                    {
                                        label: 'Address',
                                        value: `${viewBooking.poojaAddress}, ${viewBooking.poojaCity}, ${viewBooking.poojaState}${viewBooking.poojaPincode ? ' - ' + viewBooking.poojaPincode : ''}`
                                    },
                                    {
                                        label: 'Instructions',
                                        value: viewBooking.specialInstructions
                                            || 'None'
                                    },
                                    {
                                        label: 'Payment mode',
                                        value: paymentModeLabel(
                                            viewBooking.paymentMode)
                                    },
                                    {
                                        label: 'Poojari fees',
                                        value: formatPaise(viewBooking.poojariFee || 0)
                                    },
                                    {
                                        label: 'Samagri',
                                        value: viewBooking.samagriIncluded
                                            ? formatPaise(viewBooking.samagriPrice)
                                            : 'You arrange'
                                    },
                                    {
                                        label: 'Total amount',
                                        value: formatPaise(viewBooking.bookingAmount || 0)
                                    },
                                    {
                                        label: 'Paid',
                                        value: formatPaise(viewBooking.paidAmount || 0)
                                    },
                                    {
                                        label: 'Remaining',
                                        value: formatPaise(viewBooking.remainingAmount || 0)
                                    },
                                    {
                                        label: 'Payment status',
                                        value: viewBooking.paymentStatus
                                    },
                                    {
                                        label: 'Booked on',
                                        value: viewBooking.createdAt
                                            ? new Date(viewBooking.createdAt)
                                                .toLocaleString()
                                            : '-'
                                    }
                                ].map((row, i) => (
                                    <div key={i}
                                        className="d-flex justify-content-between py-2"
                                        style={{
                                            borderBottom: '1px solid #eee',
                                            fontSize: '13px'
                                        }}>
                                        <span className="text-muted">
                                            {row.label}
                                        </span>
                                        <strong className="text-end"
                                            style={{
                                                color: DARK_RED,
                                                maxWidth: '60%'
                                            }}>
                                            {row.value}
                                        </strong>
                                    </div>
                                ))}

                                <div className="mt-4">
                                    {canModify(viewBooking) ? (
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn flex-fill fw-bold"
                                                style={{
                                                    backgroundColor: RED,
                                                    color: GOLD
                                                }}
                                                onClick={() =>
                                                    openEditModal(
                                                        viewBooking)}>
                                                Edit booking
                                            </button>
                                            <button
                                                className="btn btn-outline-danger flex-fill fw-bold"
                                                onClick={() => {
                                                    setCancelId(
                                                        viewBooking.id);
                                                    setCancelReason('');
                                                }}>
                                                Cancel booking
                                            </button>
                                        </div>
                                    ) : viewBooking.bookingStatus
                                            === 'COMPLETED' ? (
                                        <div className="p-3 text-center"
                                            style={{
                                                backgroundColor: '#EAF3DE',
                                                borderRadius: '10px'
                                            }}>
                                            <p className="mb-0"
                                                style={{
                                                    color: '#27500A',
                                                    fontSize: '13px'
                                                }}>
                                                🎉 This pooja is complete.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="p-3 text-center"
                                            style={{
                                                backgroundColor: '#FCEBEB',
                                                borderRadius: '10px'
                                            }}>
                                            <p className="mb-0"
                                                style={{
                                                    color: '#791F1F',
                                                    fontSize: '13px'
                                                }}>
                                                🔒 This pooja is within
                                                24 hours. It can no longer
                                                be edited or cancelled.
                                                Please contact the poojari
                                                directly.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== PAYMENT MODAL ===== */}
            {payBooking && (
                <>
                    <div onClick={() => setPayBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%', maxWidth: '420px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Complete Payment
                            </div>
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between mb-3">
                                    <span className="text-muted">
                                        Amount
                                    </span>
                                    <strong style={{
                                        fontSize: '20px',
                                        color: RED
                                    }}>
                                        {formatPaise(getDueAmount(payBooking))}
                                    </strong>
                                </div>

                                <label className="form-label fw-bold"
                                    style={{ color: DARK_RED }}>
                                    Payment Method
                                </label>
                                <select
                                    className="form-select mb-3"
                                    value={payMethod}
                                    onChange={e =>
                                        setPayMethod(e.target.value)}
                                    style={{ borderColor: GOLD }}>
                                    <option value="UPI">UPI</option>
                                    <option value="CASH">Cash</option>
                                    <option value="CREDIT_CARD">
                                        Credit Card
                                    </option>
                                    <option value="DEBIT_CARD">
                                        Debit Card
                                    </option>
                                    <option value="NET_BANKING">
                                        Net Banking
                                    </option>
                                </select>

                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary flex-fill"
                                        onClick={() =>
                                            setPayBooking(null)}
                                        disabled={paying}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn flex-fill fw-bold"
                                        style={{
                                            backgroundColor: RED,
                                            color: GOLD
                                        }}
                                        onClick={confirmPayment}
                                        disabled={paying}>
                                        {paying
                                            ? 'Processing...'
                                            : 'Pay Now'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== CANCEL MODAL ===== */}
            {cancelId && (
                <>
                    <div onClick={() => setCancelId(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1070
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1080, width: '90%', maxWidth: '420px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Cancel Booking
                            </div>
                            <div className="card-body p-4">
                                                                {/* ⚠️ Refund ki soochna SIRF tab jab paisa diya ho.
                                    Cash booking mein kuch diya hi nahi - "refund
                                    aayega" likhna user ko bhramit karega.

                                    ⚠️ Fee wali line sirf CONFIRMED pe - poojari ne
                                    accept kar liya, uska time block ho gaya.
                                    PENDING pe koi fee nahi katti.

                                    ⚠️ "5-7 working days" isliye ki Razorpay se bank
                                    tak itna lagta hai. Bina bataye user do din baad
                                    hi poochne aa jaata hai. */}
                                {(() => {
                                    const b = bookings.find(x => x.id === cancelId);
                                    if (!b || !b.paidAmount || b.paidAmount <= 0) {
                                        return null;
                                    }
                                    const confirmed = b.bookingStatus === 'CONFIRMED';
                                    return (
                                        <div className="alert alert-warning py-2 px-3 mb-3"
                                            style={{ fontSize: '14px' }}>
                                            {confirmed
                                                ? 'A small cancellation charge applies. The rest will be refunded to you.'
                                                : 'You will receive a full refund.'}
                                            <div className="mt-1">
                                                Refunds usually reach your account in
                                                5-7 working days.
                                            </div>
                                        </div>
                                    );
                                })()}
                                <label className="form-label fw-bold"
                                    style={{ color: DARK_RED }}>
                                    Reason
                                </label>
                                <textarea
                                    className="form-control mb-3"
                                    rows="3"
                                    autoFocus
                                    placeholder="Why are you cancelling?"
                                    value={cancelReason}
                                    onChange={e =>
                                        setCancelReason(e.target.value)}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary flex-fill"
                                        onClick={() => setCancelId(null)}
                                        disabled={cancelling}>
                                        Close
                                    </button>
                                    <button
                                        className="btn btn-danger flex-fill fw-bold"
                                        onClick={confirmCancel}
                                        disabled={cancelling}>
                                        {cancelling
                                            ? 'Cancelling...'
                                            : 'Confirm'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== EDIT BOOKING MODAL ===== */}
            {editBooking && (
                <>
                    <div onClick={() => setEditBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%', maxWidth: '520px',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Edit Booking · {editBooking.poojaType}
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={confirmEdit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold"
                                                style={{ color: DARK_RED }}>
                                                Pooja Date
                                            </label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={editForm.poojaDate}
                                                min={new Date()
                                                    .toISOString()
                                                    .split('T')[0]}
                                                onChange={e =>
                                                    setEditForm({
                                                        ...editForm,
                                                        poojaDate: e.target.value
                                                    })}
                                                required
                                                style={{ borderColor: GOLD }}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold"
                                                style={{ color: DARK_RED }}>
                                                Pooja Time
                                            </label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={editForm.poojaTime}
                                                onChange={e =>
                                                    setEditForm({
                                                        ...editForm,
                                                        poojaTime: e.target.value
                                                    })}
                                                required
                                                style={{ borderColor: GOLD }}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold"
                                            style={{ color: DARK_RED }}>
                                            Address
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.poojaAddress}
                                            onChange={e =>
                                                setEditForm({
                                                    ...editForm,
                                                    poojaAddress: e.target.value
                                                })}
                                            required
                                            style={{ borderColor: GOLD }}
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold"
                                                style={{ color: DARK_RED }}>
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editForm.poojaCity}
                                                onChange={e =>
                                                    setEditForm({
                                                        ...editForm,
                                                        poojaCity: e.target.value
                                                    })}
                                                required
                                                style={{ borderColor: GOLD }}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold"
                                                style={{ color: DARK_RED }}>
                                                State
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editForm.poojaState}
                                                onChange={e =>
                                                    setEditForm({
                                                        ...editForm,
                                                        poojaState: e.target.value
                                                    })}
                                                required
                                                style={{ borderColor: GOLD }}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label fw-bold"
                                                style={{ color: DARK_RED }}>
                                                Pincode
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editForm.poojaPincode}
                                                onChange={e =>
                                                    setEditForm({
                                                        ...editForm,
                                                        poojaPincode: e.target.value
                                                    })}
                                                maxLength="6"
                                                style={{ borderColor: GOLD }}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-bold"
                                            style={{ color: DARK_RED }}>
                                            Special Instructions
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={editForm.specialInstructions}
                                            onChange={e =>
                                                setEditForm({
                                                    ...editForm,
                                                    specialInstructions:
                                                        e.target.value
                                                })}
                                            style={{ borderColor: GOLD }}>
                                        </textarea>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary flex-fill"
                                            onClick={() =>
                                                setEditBooking(null)}
                                            disabled={savingEdit}>
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn flex-fill fw-bold"
                                            style={{
                                                backgroundColor: RED,
                                                color: GOLD
                                            }}
                                            disabled={savingEdit}>
                                            {savingEdit
                                                ? 'Saving...'
                                                : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== REVIEW MODAL ===== */}
            {reviewBooking && (
                <>
                    <div onClick={() => setReviewBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%', maxWidth: '460px',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                ⭐ Rate your experience
                            </div>
                            <div className="card-body p-4">
                                <p className="text-muted mb-3"
                                    style={{ fontSize: '13px' }}>
                                    {reviewBooking.poojaType} ·{' '}
                                    {reviewBooking.poojaDate}
                                </p>

                                <form onSubmit={confirmReview}>

                                    <div className="mb-3 text-center">
                                        <label className="form-label fw-bold d-block"
                                            style={{ color: DARK_RED }}>
                                            Your Rating
                                        </label>
                                        <div>
                                            {[1, 2, 3, 4, 5].map(star => (
                                                <span
                                                    key={star}
                                                    onClick={() =>
                                                        setReviewForm({
                                                            ...reviewForm,
                                                            rating: star
                                                        })}
                                                    style={{
                                                        fontSize: '34px',
                                                        cursor: 'pointer',
                                                        marginRight: '4px',
                                                        filter: star <= reviewForm.rating
                                                            ? 'none'
                                                            : 'grayscale(100%)',
                                                        opacity: star <= reviewForm.rating
                                                            ? 1 : 0.35
                                                    }}>
                                                    ⭐
                                                </span>
                                            ))}
                                        </div>
                                        <small className="text-muted">
                                            {reviewForm.rating === 5
                                                ? 'Excellent'
                                                : reviewForm.rating === 4
                                                ? 'Very good'
                                                : reviewForm.rating === 3
                                                ? 'Good'
                                                : reviewForm.rating === 2
                                                ? 'Fair'
                                                : 'Poor'}
                                        </small>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold"
                                            style={{ color: DARK_RED }}>
                                            Title
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Excellent pooja service"
                                            value={reviewForm.title}
                                            onChange={e =>
                                                setReviewForm({
                                                    ...reviewForm,
                                                    title: e.target.value
                                                })}
                                            required
                                            style={{ borderColor: GOLD }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-bold"
                                            style={{ color: DARK_RED }}>
                                            Your Review
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            placeholder="How was the poojari? Was he punctual and knowledgeable?"
                                            value={reviewForm.comment}
                                            onChange={e =>
                                                setReviewForm({
                                                    ...reviewForm,
                                                    comment: e.target.value
                                                })}
                                            style={{ borderColor: GOLD }}>
                                        </textarea>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary flex-fill"
                                            onClick={() =>
                                                setReviewBooking(null)}
                                            disabled={savingReview}>
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn flex-fill fw-bold"
                                            style={{
                                                backgroundColor: RED,
                                                color: GOLD
                                            }}
                                            disabled={savingReview}>
                                            {savingReview
                                                ? 'Submitting...'
                                                : 'Submit Review'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Home;